import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { R as GeneralResources } from '@/Resources.resx';
import { R } from '@/features/programs/resources/ProgramsResources.resx';

import { NnSundVaegtProgramDto } from '@/shared/interfaces/api/sundvaegt-interfaces';
import { fetchNnSundvaegtProgramAtom } from './fetchNnSundvaegtProgramAtom';
import { deleteNnWeightLog } from '../api/deleteNnWeightLog';

const queryKey = ['nnsundvaegt-weightprogram'] as const;

export const deleteNnWeightLogAtom = atomWithMutation<
    string, // TData (return type)
    number, // TVariables (you pass weightLog Id)
    Error, // TError
    { previous?: NnSundVaegtProgramDto } // TContext (used for rollback)
>((get) => {
    const queryClient = get(queryClientAtom);

    return {
        mutationFn: async (weightLogId: number) => {
            // read the freshest program right before calling the API
            const program = get(fetchNnSundvaegtProgramAtom).data;

            if (!program) {
                throw new Error('No SundVægt Sammen program loaded');
            }
            try {
                return await deleteNnWeightLog(weightLogId);
            } catch (error) {
                throw new Error(
                    `${R.SundvaegtDeleteWeightLogErrorMessage}: ${
                        error instanceof Error
                            ? error.message
                            : GeneralResources.UnknownError
                    }`,
                );
            }
        },

        // optimistic update
        onMutate: async (weightLogId: number) => {
            await queryClient.cancelQueries({ queryKey });

            const previous =
                queryClient.getQueryData<NnSundVaegtProgramDto>(queryKey);

            if (previous) {
                queryClient.setQueryData<NnSundVaegtProgramDto>(queryKey, {
                    ...previous,
                    WeightLogs: previous.WeightLogs.filter(
                        (log) => log.Id !== weightLogId,
                    ),
                });
            }
            return { previous };
        },

        // rollback on error
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) {
                queryClient.setQueryData<NnSundVaegtProgramDto>(
                    queryKey,
                    ctx.previous,
                );
            }
        },

        // ensure server truth after success
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey });
        },
    };
});
